import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "bg-transparent text-xs font-medium text-gray-500 dark:text-white hover:bg-black/5 dark:hover:bg-white/20 outline focus:outline-1 outline-offset-0 outline-1 outline-gray-300 dark:outline-white/20",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        ghost_table: "hover:bg-gray-100 active:bg-gray-100",
        outline_xcausal:
          "font-base text-white ring-[1px] ring-white bg-transparent hover:bg-transparent",
        fill: "focus-visible:bg-[#355272] bg-[#355272] dark:bg-[#355272]]",
        headless: "",
      },
      size: {
        default: "h-10 px-4 py-2 rounded-md",
        xs: "text-xs h-8 px-3 rounded-lg",
        sm: "text-sm h-9 px-3 rounded-md",
        normal: "text-sm h-10 px-4 rounded-lg",
        lg: "h-11 px-8 rounded-md",
        icon: "h-8 w-8 rounded-lg",
        xs_icon: "h-7 w-7 rounded-lg",
        headless: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
